import React, { useState, useCallback, memo } from 'react';
import { ChevronDown, ArrowLeft, Upload, Loader2 } from 'lucide-react';
import { NodeType, NodeStatus, ImageGenerationMode } from '@/types';
import { useModelStore } from '@/stores/modelStore';
import {
  UPSCALE_RESOLUTIONS,
} from './ParamSelectors';
import { PromptEditor } from './editor/PromptEditor';
import { ModelConfigEditor } from './editor/ModelConfigEditor';
import { AdvancedParamsEditor } from './editor/AdvancedParamsEditor';
import { GenerationPreview } from './editor/GenerationPreview';

interface EditorPanelProps {
  nodeId: string;
  nodeType: NodeType;
  status: NodeStatus;
  prompt: string;
  model?: string;
  aspectRatio?: string;
  resolution?: string;
  duration?: number;
  imageCount?: number;
  videoCount?: number;
  upscaleStrength?: number;
  generateAudio?: boolean;
  inputAssets: { id: string; type: 'image' | 'video' | 'audio'; src: string }[];
  isOpen: boolean;
  currentImageMode?: ImageGenerationMode;
  onPromptChange: (value: string) => void;
  onPromptSubmit: () => void;
  onUpdate: (id: string, data: any) => void;
  onGenerate: () => void;
  onImageModeSwitch?: (mode: ImageGenerationMode) => void;
  onAddUploadNode?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const GLASS_PANEL = "bg-[#2c2c2e]/95 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-2xl";

const EditorPanelInner: React.FC<EditorPanelProps> = ({
  nodeId,
  nodeType,
  status,
  prompt,
  model,
  aspectRatio,
  resolution,
  duration,
  imageCount,
  videoCount,
  upscaleStrength,
  generateAudio,
  inputAssets,
  isOpen,
  currentImageMode,
  onPromptChange,
  onPromptSubmit,
  onUpdate,
  onGenerate,
  onImageModeSwitch,
  onAddUploadNode,
  className = '',
  style,
}) => {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [currentMode, setCurrentMode] = useState<string>('');
  const isWorking = status === NodeStatus.WORKING;
  const isUpscaleMode = nodeType === NodeType.IMAGE_GENERATOR && currentImageMode === 'UPSCALE';
  const getModelsByMode = useModelStore((s) => s.getModelsByMode);
  const getModelById = useModelStore((s) => s.getModelById);

  const getModeForNodeType = (type: NodeType): string => {
    switch (type) {
      case NodeType.VIDEO_GENERATOR: {
        const videoMode = currentMode || (inputAssets.length > 0 ? 'omni' : 'txt2video');
        return videoMode;
      }
      case NodeType.VIDEO_ANALYZER: return 'analyze';
      case NodeType.AUDIO_GENERATOR: return 'audio';
      case NodeType.IMAGE_EDITOR: return 'img2img';
      case NodeType.IMAGE_GENERATOR: return currentImageMode === 'TXT2IMG' ? 'txt2img' : 'img2img';
      case NodeType.PANORAMA_GENERATOR: {
        const hasInput = inputAssets.length > 0;
        return currentMode || (hasInput ? 'img2panorama' : 'txt2panorama');
      }
      default: return 'txt2img';
    }
  };

  const mode = getModeForNodeType(nodeType);
  const models = getModelsByMode(mode).map(m => ({ l: m.name, v: m.id }));
  const currentModel = model ? getModelById(model) : undefined;
  const modelSupportedResolutions: string[] = currentModel?.supportedResolutions || [];

  const handleUpdate = useCallback((id: string, data: any) => {
    if (data.model && data.model !== model) {
      const newModel = getModelById(data.model);
      const newSupported = newModel?.supportedResolutions || [];
      if (newSupported.length > 0 && resolution) {
        const currentRes = resolution.toLowerCase();
        const isSupported = newSupported.some(r => r.toLowerCase() === currentRes);
        if (!isSupported) {
          data.resolution = newSupported[0];
        }
      }
    }
    onUpdate(id, data);
  }, [model, resolution, onUpdate]);

  if (!isOpen) return null;

  return (
    <div
      className={`${GLASS_PANEL} ${isUpscaleMode ? 'p-3' : 'p-4'} flex flex-col gap-3 transition-all duration-300 nodrag nowheel ${className}`}
      style={style}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
    >
      <PromptEditor
        nodeId={nodeId}
        nodeType={nodeType}
        prompt={prompt}
        currentImageMode={currentImageMode}
        inputAssets={inputAssets}
        isWorking={isWorking}
        currentMode={currentMode}
        onPromptChange={onPromptChange}
        onPromptSubmit={onPromptSubmit}
        onImageModeSwitch={onImageModeSwitch}
        onAddUploadNode={onAddUploadNode}
        onUpdate={onUpdate}
        onSetCurrentMode={setCurrentMode}
        onSetIsInputFocused={setIsInputFocused}
        onGenerate={onGenerate}
      />

      {isUpscaleMode ? (
        <div className="flex flex-col gap-3">
          <div className="text-[13px] font-medium text-white">高清放大</div>
          <ModelConfigEditor
            nodeId={nodeId}
            model={model}
            models={models}
            onUpdate={handleUpdate}
            layout="vertical"
          />
          <div className="flex items-center justify-between gap-4">
            <span className="text-[12px] text-white w-12 shrink-0">模型</span>
            <div className="relative group/upscale-submodel" style={{ width: '300px' }}>
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] border border-white/[0.10] cursor-pointer transition-colors text-[12px] font-medium text-white hover:bg-white/10">
                <span className="flex-1">通用</span>
                <ChevronDown size={12} className="text-white/40" />
              </div>
              <div className="absolute top-full right-0 pt-2 w-full opacity-0 -translate-y-2 pointer-events-none group-hover/upscale-submodel:opacity-100 group-hover/upscale-submodel:translate-y-0 group-hover/upscale-submodel:pointer-events-auto transition-all duration-200 z-[200]">
                <div className="bg-[#1c1c1e] border border-white/10 rounded-xl shadow-xl overflow-hidden">
                  {['通用', '人像', '风景', '建筑', '动漫'].map(s => (
                    <div key={s} className="px-3 py-2 text-xs font-medium cursor-pointer hover:bg-white/10 text-white/60">{s}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-[12px] text-white w-12 shrink-0">放大倍数</span>
            <div className="flex gap-2" style={{ width: '300px' }}>
              {[2, 4, 6].map(n => (
                <button
                  key={n}
                  onClick={() => onUpdate(nodeId, { resolution: `${n}k` })}
                  className={`flex-1 py-2 rounded-xl text-[12px] font-medium transition-all border ${
                    (resolution || '2k') === `${n}k`
                      ? 'bg-white/20 text-white border-white/40 shadow-[0_0_8px_rgba(255,255,255,0.1)]'
                      : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <GenerationPreview
            isWorking={isWorking}
            onGenerate={onGenerate}
            isUpscaleMode
            onBackToImg2Img={onImageModeSwitch ? () => onImageModeSwitch('IMG2IMG') : undefined}
          />
        </div>
      ) : (
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <ModelConfigEditor
              nodeId={nodeId}
              model={model}
              models={models}
              onUpdate={handleUpdate}
            />
            <AdvancedParamsEditor
              nodeId={nodeId}
              nodeType={nodeType}
              aspectRatio={aspectRatio}
              resolution={resolution}
              duration={duration}
              imageCount={imageCount}
              videoCount={videoCount}
              upscaleStrength={upscaleStrength}
              generateAudio={generateAudio}
              generationMode={currentImageMode}
              modelSupportedResolutions={modelSupportedResolutions}
              onUpdate={handleUpdate}
            />
          </div>
          <GenerationPreview
            isWorking={isWorking}
            onGenerate={onGenerate}
          />
        </div>
      )}
    </div>
  );
};

export const EditorPanel = memo(EditorPanelInner, (prevProps, nextProps) => {
  const prevAssets = prevProps.inputAssets;
  const nextAssets = nextProps.inputAssets;
  const assetsEqual = prevAssets === nextAssets || (
    Array.isArray(prevAssets) && Array.isArray(nextAssets) &&
    prevAssets.length === nextAssets.length &&
    prevAssets.every((a, i) => a.id === nextAssets[i].id && a.type === nextAssets[i].type && a.src === nextAssets[i].src)
  );

  return (
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.nodeType === nextProps.nodeType &&
    prevProps.status === nextProps.status &&
    prevProps.prompt === nextProps.prompt &&
    prevProps.model === nextProps.model &&
    prevProps.aspectRatio === nextProps.aspectRatio &&
    prevProps.resolution === nextProps.resolution &&
    prevProps.duration === nextProps.duration &&
    prevProps.imageCount === nextProps.imageCount &&
    prevProps.videoCount === nextProps.videoCount &&
    prevProps.upscaleStrength === nextProps.upscaleStrength &&
    prevProps.generateAudio === nextProps.generateAudio &&
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.currentImageMode === nextProps.currentImageMode &&
    assetsEqual
  );
});
